import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Snackbar, Stack, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { diagnosticarFlespi, diagnosticarUnidadWialon, reiniciarFlespi } from "../../../services/modems";
import { useAuth } from "../../../context/AuthContext";
import MapaRecorridoDiagnostico, { MapaPosiciones } from "./MapaRecorridoDiagnostico";

const ESTADOS = {
  ok: { color: "success", Icono: CheckCircleOutlineIcon },
  alerta: { color: "warning", Icono: WarningAmberIcon },
  falla: { color: "error", Icono: ErrorOutlineIcon },
};
const CERTEZA = { alta: "Certeza alta", media: "Certeza media", baja: "Certeza baja" };
const COLOR_EVIDENCIA = { error: "error.main", alerta: "warning.main", info: "text.secondary" };
const MINUTOS_REPORTANDO = 60; // mismo umbral que el backend
// Paleta categórica validada (slots 1 y 2) para claro / oscuro.
const SERIES = { light: ["#2a78d6", "#eb6834"], dark: ["#3987e5", "#d95926"] };

const fechaHora = (ts, conSegundos = false) => {
  if (!ts) return "-";
  return new Date(Number(ts) * 1000).toLocaleString("es-BO", {
    timeZone: "America/La_Paz", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", ...(conSegundos ? { second: "2-digit" } : {}),
  });
};
const horaCorta = (ts) => new Date(Number(ts) * 1000).toLocaleString("es-BO", { timeZone: "America/La_Paz", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const duracion = (seg) => {
  const s = Math.max(0, Math.round(Number(seg)));
  if (s < 90) return `${s} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 48 ? `${h} h ${min % 60} min` : `${Math.floor(h / 24)} días`;
};
const hace = (ts) => duracion(Date.now() / 1000 - Number(ts));
const reciente = (ts) => !!ts && Date.now() / 1000 - Number(ts) <= MINUTOS_REPORTANDO * 60;
const valor = (v, sufijo = "") => (v === null || v === undefined ? "-" : `${Math.round(v * 100) / 100}${sufijo}`);
const lista = (items) => items.map((o) => o.nombre).join(", ").replace(/, ([^,]*)$/, " y $1");
const mapsUrl = (u) => `https://www.google.com/maps?q=${u.latitud},${u.longitud}`;
const mediana = (xs) => {
  if (!xs.length) return null;
  const o = [...xs].sort((a, b) => a - b);
  return o[Math.floor(o.length / 2)];
};

// Cada cuánto reporta (mediana entre mensajes) y los huecos: tramos sin mensajes bastante más largos que lo normal.
const analizarFrecuencia = (mensajes = []) => {
  const ts = mensajes.map((m) => Number(m.ts)).filter(Boolean).sort((a, b) => b - a);
  const difs = ts.slice(1).map((t, i) => ts[i] - t).filter((d) => d > 0);
  const intervalo = mediana(difs);
  if (!intervalo) return { intervalo: null, huecos: [], umbral: 15 * 60 };
  const umbral = Math.max(intervalo * 3, 15 * 60);
  const huecos = ts.slice(1)
    .map((t, i) => ({ desde: t, hasta: ts[i], duracion: ts[i] - t }))
    .filter((h) => h.duracion >= umbral)
    .slice(0, 10);
  return { intervalo, huecos, umbral };
};

// Respaldo para Wialon / LeaseControl si la consulta de mensajes falla: solo con el último mensaje que ya trae la tarjeta.
const diagnosticoExterno = (plataforma, hayFlespi) => {
  const p = plataforma.params || {};
  const evidencias = [];
  if (p.voltage !== undefined) evidencias.push({ nivel: Number(p.voltage) < 3 ? "alerta" : "info", texto: `Alimentación en el último mensaje: ${p.voltage} V.` });
  if (p.gsm !== undefined) evidencias.push({ nivel: "info", texto: `Señal GSM en el último mensaje: ${p.gsm}.` });
  if (p.gps_valid === false || ["L", "V"].includes(String(p.gps_valid).toUpperCase())) evidencias.push({ nivel: "alerta", texto: "El último mensaje llegó sin posición GPS válida." });
  const base = { evidencias, ultimo_mensaje: plataforma.ts || null, parametros: Object.keys(p).length ? p : null };
  if (!plataforma.ts) {
    return { ...base, estado: "falla", certeza: "alta", titulo: `${plataforma.nombre} nunca recibió datos`, causa: `La unidad existe en ${plataforma.nombre} pero no tiene ningún mensaje.`, recomendacion: "Revisar que el identificador de la unidad coincida con el del equipo y que el reenvío desde flespi esté configurado." };
  }
  if (reciente(plataforma.ts)) {
    return { ...base, estado: "ok", certeza: "alta", titulo: "Reportando normalmente", causa: `El último mensaje llegó a ${plataforma.nombre} hace ${hace(plataforma.ts)}.`, recomendacion: "" };
  }
  return {
    ...base, estado: "falla", certeza: "baja", titulo: `Sin datos en ${plataforma.nombre}`,
    causa: `${plataforma.nombre} no recibe mensajes desde ${fechaHora(plataforma.ts)} (hace ${hace(plataforma.ts)}). No se pudieron cargar los mensajes de la unidad, solo se usa el último.`,
    recomendacion: hayFlespi ? "Usar «Diagnosticar» en la tarjeta de Flespi para ver la causa (conexiones, alimentación, señal y chip)." : "Revisar el equipo en sitio o enviarle un comando por SMS.",
  };
};

const Indicador = ({ etiqueta, valor: texto, detalle, tono }) => (
  <Paper variant="outlined" sx={{ px: 1, py: 0.6, minWidth: 108, flex: "1 1 108px" }}>
    <Typography sx={{ fontSize: 10, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 900, color: tono || "text.primary", fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }} noWrap title={texto}>{texto}</Typography>
    {detalle && <Typography sx={{ fontSize: 10.5, color: "text.secondary" }} noWrap title={detalle}>{detalle}</Typography>}
  </Paper>
);

const celdaSx = { "& .MuiTableCell-root": { py: 0.35, px: 0.75, fontSize: 11, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } };
const Vacio = ({ children }) => <Typography sx={{ fontSize: 12, color: "text.secondary", py: 2, textAlign: "center" }}>{children}</Typography>;

const GraficoSerie = ({ titulo, datos, clave, unidad, color }) => {
  const puntos = datos.filter((d) => d[clave] !== null && d[clave] !== undefined);
  if (puntos.length < 2) return null;
  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 800, mb: 0.3 }}>{titulo}</Typography>
      <Box sx={{ height: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={puntos} margin={{ top: 4, right: 12, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
            <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={horaCorta} tick={{ fontSize: 10 }} minTickGap={40} />
            <YAxis tick={{ fontSize: 10 }} width={44} unit={unidad} domain={["auto", "auto"]} />
            <RTooltip labelFormatter={(ts) => fechaHora(ts, true)} formatter={(v) => [`${Math.round(v * 100) / 100}${unidad}`, titulo]} contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey={clave} stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

// Roles que pueden reiniciar equipos (el backend valida la misma lista).
const ROLES_REINICIO = ["admin", "sistemas", "gerente"];

// Diagnóstico de reporte de UNA plataforma (la tarjeta donde se pulsó), comparada con las demás donde está el equipo:
// así se ve tanto "aquí no llega pero reporta en otra" (unidad olvidada tras mover el equipo) como la causa del corte.
// plataforma: { clave, nombre, tipo: "flespi" | "wialon" | "leasecontrol", id, secundaria, ts, params }
// todas: [{ clave, nombre, ts }] último dato de cada plataforma encontrada.
export default function DiagnosticoReporteDialog({ plataforma, todas = [], onClose }) {
  const theme = useTheme();
  const [remoto, setRemoto] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [fallo, setFallo] = useState(false);
  const [cantidad, setCantidad] = useState(100);
  const [recarga, setRecarga] = useState(0);
  const [pestana, setPestana] = useState("evidencias");
  const [filtroCrudos, setFiltroCrudos] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [confirmarReinicio, setConfirmarReinicio] = useState(false);
  const [reiniciando, setReiniciando] = useState(false);
  const [avisoReinicio, setAvisoReinicio] = useState(null); // { severidad, texto }
  const { user } = useAuth();
  const esFlespi = plataforma?.tipo === "flespi";

  useEffect(() => {
    if (plataforma) { setPestana("evidencias"); setCantidad(100); setFiltroCrudos(""); }
  }, [plataforma]);

  useEffect(() => {
    setError("");
    setFallo(false);
    if (!plataforma?.id) { setRemoto(null); return undefined; }
    const controller = new AbortController();
    setCargando(true);
    const consulta = esFlespi
      ? diagnosticarFlespi(plataforma.id, { secundaria: plataforma.secundaria, cantidad, signal: controller.signal })
      : diagnosticarUnidadWialon(plataforma.tipo, plataforma.id, { cantidad, signal: controller.signal });
    consulta
      .then((r) => setRemoto(r.data?.data || null))
      .catch((e) => {
        if (controller.signal.aborted) return;
        setRemoto(null);
        if (esFlespi) setError(e?.response?.data?.message || "No se pudo diagnosticar el dispositivo.");
        else setFallo(true); // Wialon / LeaseControl: se sigue con el último mensaje de la tarjeta
      })
      .finally(() => { if (!controller.signal.aborted) setCargando(false); });
    return () => controller.abort();
  }, [esFlespi, plataforma, cantidad, recarga]);

  const base = useMemo(() => {
    if (!plataforma) return null;
    if (remoto) return remoto;
    if (!esFlespi && (fallo || !plataforma.id)) return diagnosticoExterno(plataforma, todas.some((o) => o.clave.startsWith("flespi")));
    return null;
  }, [plataforma, esFlespi, remoto, fallo, todas]);

  const otras = todas.filter((o) => o.clave !== plataforma?.clave);
  const ultimoPropio = base?.ultimo_mensaje ?? plataforma?.ts;
  const otrasReportando = otras.filter((o) => reciente(o.ts));
  const otrasSinDatos = otras.filter((o) => !reciente(o.ts));

  // La comparación entre plataformas manda sobre la causa técnica: si el equipo reporta en otra plataforma está vivo,
  // y lo que pasa es que esta unidad quedó sin uso (o el reenvío hacia aquí no funciona).
  const resultado = (() => {
    if (!base) return null;
    if (!reciente(ultimoPropio) && otrasReportando.length) {
      const masReciente = otrasReportando.reduce((a, b) => (Number(b.ts) > Number(a.ts) ? b : a));
      return {
        ...base, estado: "alerta", certeza: "alta", titulo: `El equipo reporta en ${lista(otrasReportando)}, no en ${plataforma.nombre}`,
        causa: `El equipo está activo (último dato en ${masReciente.nombre} hace ${hace(masReciente.ts)}), pero a ${plataforma.nombre} no llega nada ${ultimoPropio ? `desde ${fechaHora(ultimoPropio)}` : "nunca"}.`,
        recomendacion: `Si el equipo se pasó a ${lista(otrasReportando)}, desactivar o eliminar esta unidad en ${plataforma.nombre} para que no quede duplicada. Si debería recibir datos aquí, revisar el reenvío y el identificador de la unidad.`,
      };
    }
    if (reciente(ultimoPropio) && otrasSinDatos.length) {
      return {
        ...base, estado: base.estado === "ok" ? "alerta" : base.estado,
        titulo: base.estado === "ok" ? `Reporta en ${plataforma.nombre}, pero ${lista(otrasSinDatos)} sin datos` : base.titulo,
        recomendacion: base.estado === "ok"
          ? `Si ${lista(otrasSinDatos)} ya no se usa(n) para este equipo, desactivarla(s); si deberían recibir datos, revisar el reenvío desde flespi.`
          : base.recomendacion,
      };
    }
    return base;
  })();

  const mensajesBase = base?.mensajes;
  const mensajes = useMemo(() => mensajesBase || [], [mensajesBase]);
  const conexiones = resultado?.conexiones || [];
  const frecuencia = useMemo(() => analizarFrecuencia(mensajes), [mensajes]);
  const ultimo = mensajes[0] || null;
  const grafico = useMemo(() => [...mensajes].reverse(), [mensajes]);
  const crudos = useMemo(() => {
    const p = resultado?.parametros || {};
    const f = filtroCrudos.trim().toLowerCase();
    return Object.entries(p)
      .filter(([k, v]) => !f || k.toLowerCase().includes(f) || String(v).toLowerCase().includes(f))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [resultado?.parametros, filtroCrudos]);
  const em = resultado?.emnify;
  const colores = SERIES[theme.palette.mode === "dark" ? "dark" : "light"];
  const { color, Icono } = ESTADOS[resultado?.estado] || ESTADOS.alerta;
  const nombreEquipo = resultado?.dispositivo?.nombre || resultado?.unidad?.nombre || "";
  const iconoUnidad = resultado?.unidad?.icono || null; // Wialon / LeaseControl
  // Con posiciones válidas se dibuja el recorrido; si no, la última posición GPS válida (puede ser de días atrás) y la
  // ubicación del chip según Emnify.
  const conRecorrido = mensajes.some((m) => m.latitud && m.longitud && m.gps_valido !== false);
  const fixGps = resultado?.ultima_posicion_gps || null;
  const ubicacionEmnify = resultado?.emnify?.ubicacion || null;
  const conPosicion = conRecorrido || !!fixGps || !!ubicacionEmnify;
  // Estado del GPS según el último mensaje que lo informa (los heartbeats de Concox traen gnss sin posición).
  const ultimoGps = mensajes.find((m) => m.gps_valido !== null || (m.gnss !== null && m.gnss !== undefined)) || null;
  const gpsOk = ultimoGps ? ultimoGps.gps_valido !== false && ultimoGps.gnss !== false && ultimoGps.satelites !== 0 : null;
  const identEquipo = resultado?.dispositivo?.ident || resultado?.unidad?.uid || "";

  const copiarReporte = async () => {
    if (!resultado) return;
    const lineas = [
      `Diagnóstico de reporte · ${plataforma.nombre}${nombreEquipo ? ` · ${nombreEquipo}` : ""}${identEquipo && identEquipo !== nombreEquipo ? ` (${identEquipo})` : ""}${resultado.modelo ? ` · ${resultado.modelo}` : ""}`,
      `Estado: ${resultado.titulo} (${(CERTEZA[resultado.certeza] || "").toLowerCase()})`,
      `Causa: ${resultado.causa}`,
      resultado.recomendacion && `Qué hacer: ${resultado.recomendacion}`,
      "",
      "Último dato por plataforma:",
      ...todas.map((o) => `- ${o.nombre}: ${o.ts ? `${fechaHora(o.ts)} (hace ${hace(o.ts)})` : "sin datos"}`),
      resultado.evidencias?.length ? "" : null,
      resultado.evidencias?.length ? "Evidencias:" : null,
      ...(resultado.evidencias || []).map((e) => `- ${e.texto}`),
      resultado.ubicacion ? `\nÚltima ubicación (${fechaHora(resultado.ubicacion.ts)}): ${mapsUrl(resultado.ubicacion)}` : null,
    ].filter((l) => l !== null && l !== undefined && l !== false);
    try {
      await navigator.clipboard.writeText(lineas.join("\n"));
      setCopiado(true);
    } catch {
      setError("No se pudo copiar al portapapeles.");
    }
  };

  // Reinicio: solo flespi, si el modelo tiene el comando y para los roles permitidos. Sin conexión, el comando va en cola.
  const reinicio = esFlespi && plataforma?.id && ROLES_REINICIO.includes(user?.role) ? resultado?.reinicio || null : null;
  const enCola = !resultado?.conectado;
  const reiniciar = async () => {
    setReiniciando(true);
    try {
      await reiniciarFlespi(plataforma.id, { secundaria: plataforma.secundaria, enCola });
      setAvisoReinicio(enCola
        ? { severidad: "info", texto: "Reinicio en cola: se enviará cuando el equipo se conecte (vence en 1 hora)." }
        : { severidad: "success", texto: "Comando enviado: el equipo se reiniciará en unos segundos. Vuelve a diagnosticar en 1–2 minutos." });
      setConfirmarReinicio(false);
    } catch (e) {
      setAvisoReinicio({ severidad: "error", texto: e?.response?.data?.message || "No se pudo enviar el comando de reinicio." });
      setConfirmarReinicio(false);
    } finally {
      setReiniciando(false);
    }
  };

  const pestanas = [
    { valor: "evidencias", etiqueta: "Evidencias" },
    conPosicion && {
      valor: "mapa",
      etiqueta: "Mapa",
      icono: iconoUnidad
        ? <Box component="img" src={iconoUnidad} alt="" sx={{ width: 18, height: 18, objectFit: "contain" }} />
        : <MapOutlinedIcon sx={{ fontSize: 17 }} />,
    },
    { valor: "mensajes", etiqueta: `Mensajes (${mensajes.length})` },
    { valor: "grafico", etiqueta: "Gráfico" },
    esFlespi && { valor: "conexiones", etiqueta: `Conexiones (${conexiones.length})` },
    (em || resultado?.iccid) && { valor: "chip", etiqueta: "Chip" },
    { valor: "crudos", etiqueta: "Datos crudos" },
  ].filter(Boolean);
  const pestanaActiva = pestanas.some((t) => t.valor === pestana) ? pestana : "evidencias";

  return (
    <Dialog open={!!plataforma} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 900 }}>Diagnóstico de reporte · {plataforma?.nombre}</Typography>
          {(nombreEquipo || identEquipo || resultado?.modelo) && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }} noWrap>
                {nombreEquipo}{identEquipo && identEquipo !== nombreEquipo ? ` · ${identEquipo}` : ""}{resultado?.modelo ? ` · ${resultado.modelo}` : ""}
              </Typography>
              {resultado?.solo_2g && (
                <Tooltip title="Este modelo solo funciona con red 2G: no reporta donde no hay 2G (gran parte de Chile).">
                  <Chip size="small" label="Solo 2G" color={resultado.zona_sin_2g ? "warning" : "default"} variant="outlined" sx={{ height: 18, fontSize: 10.5, fontWeight: 800 }} />
                </Tooltip>
              )}
            </Stack>
          )}
        </Box>
        <Tooltip title="Copiar el reporte (para enviarlo al técnico)">
          <span><IconButton size="small" onClick={copiarReporte} disabled={!resultado}><ContentCopyIcon fontSize="small" /></IconButton></span>
        </Tooltip>
        <Tooltip title="Volver a diagnosticar">
          <span><IconButton size="small" onClick={() => setRecarga((n) => n + 1)} disabled={cargando || !plataforma?.id}><RefreshIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        {cargando && !resultado && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3, justifyContent: "center" }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{esFlespi ? "Consultando conexiones, últimos mensajes y estado del chip…" : `Consultando los últimos mensajes en ${plataforma?.nombre}…`}</Typography>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ py: 0, mb: 1 }}>{error}</Alert>}
        {resultado && (
          <Stack spacing={1} sx={{ opacity: cargando ? 0.55 : 1, transition: "opacity 120ms" }}>
            <Paper variant="outlined" sx={(t) => ({ p: 1.25, borderColor: t.palette[color].main, bgcolor: alpha(t.palette[color].main, t.palette.mode === "dark" ? 0.12 : 0.06) })}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Icono color={color} sx={{ mt: 0.2 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 15, fontWeight: 900 }}>{resultado.titulo}</Typography>
                    <Chip size="small" label={CERTEZA[resultado.certeza] || resultado.certeza} sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, mt: 0.4 }}>{resultado.causa}</Typography>
                  {resultado.recomendacion && (
                    <Typography sx={{ fontSize: 12, mt: 0.6, color: "text.secondary" }}><b>Qué hacer:</b> {resultado.recomendacion}</Typography>
                  )}
                </Box>
              </Stack>
            </Paper>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
              <Indicador etiqueta="Último mensaje" valor={ultimoPropio ? `hace ${hace(ultimoPropio)}` : "Nunca"} detalle={ultimoPropio ? fechaHora(ultimoPropio) : null}
                tono={reciente(ultimoPropio) ? "success.main" : "error.main"} />
              {frecuencia.intervalo && <Indicador etiqueta="Reporta cada" valor={`~${duracion(frecuencia.intervalo)}`} detalle={`${frecuencia.huecos.length} hueco(s) largos`} />}
              {ultimo?.voltaje !== null && ultimo?.voltaje !== undefined && <Indicador etiqueta="Alimentación" valor={valor(ultimo.voltaje, " V")} tono={ultimo.voltaje < 3 ? "error.main" : undefined} />}
              {(ultimo?.bateria_nivel ?? ultimo?.bateria_voltaje) !== null && (ultimo?.bateria_nivel ?? ultimo?.bateria_voltaje) !== undefined && (
                <Indicador etiqueta="Batería interna" valor={ultimo.bateria_nivel !== null ? valor(ultimo.bateria_nivel, " %") : valor(ultimo.bateria_voltaje, " V")} />
              )}
              {ultimo?.senal !== null && ultimo?.senal !== undefined && <Indicador etiqueta="Señal celular" valor={`${Math.round(ultimo.senal)} %`} tono={ultimo.senal < 25 ? "warning.main" : undefined} />}
              {ultimoGps && <Indicador etiqueta="GPS" valor={gpsOk ? "Válido" : "Sin señal"} tono={gpsOk ? undefined : "warning.main"}
                detalle={!gpsOk && fixGps ? `último fix hace ${hace(fixGps.ts)}` : ultimoGps.satelites !== null && ultimoGps.satelites !== undefined ? `${ultimoGps.satelites} satélites` : null} />}
              {ultimo && ultimo.ignicion !== null && <Indicador etiqueta="Ignición" valor={ultimo.ignicion ? "Encendido" : "Apagado"} />}
            </Box>

            {todas.length > 1 && (
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 900, mb: 0.4 }}>Último dato por plataforma</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                  {todas.map((o) => {
                    const ok = reciente(o.ts);
                    return (
                      <Paper key={o.clave} variant="outlined" sx={{ px: 1, py: 0.5, minWidth: 140, borderColor: o.clave === plataforma?.clave ? "text.primary" : "divider" }}>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: ok ? "success.main" : "error.main" }} />
                          <Typography sx={{ fontSize: 11, fontWeight: 800 }}>{o.nombre}</Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: ok ? "text.primary" : "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                          {o.ts ? `${fechaHora(o.ts)} · hace ${hace(o.ts)}` : "Sin datos"}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box>
              <Tabs value={pestanaActiva} onChange={(_, v) => setPestana(v)} variant="scrollable" scrollButtons="auto"
                sx={{ minHeight: 32, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { minHeight: 32, py: 0.4, px: 1.25, fontSize: 12, textTransform: "none", fontWeight: 700 } }}>
                {pestanas.map((t) => <Tab key={t.valor} value={t.valor} label={t.etiqueta} icon={t.icono} iconPosition="start" sx={{ gap: 0.5, "& .MuiTab-iconWrapper": { mr: 0 } }} />)}
              </Tabs>
              <Box sx={{ pt: 1, minHeight: 180 }}>
                {pestanaActiva === "evidencias" && (
                  <Stack spacing={0.3}>
                    {!resultado.evidencias?.length && <Vacio>Sin evidencias adicionales.</Vacio>}
                    {(resultado.evidencias || []).map((e) => (
                      <Stack key={e.texto} direction="row" spacing={0.75} alignItems="baseline">
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: COLOR_EVIDENCIA[e.nivel] || "text.secondary", transform: "translateY(-1px)" }} />
                        <Typography sx={{ fontSize: 12, color: e.nivel === "info" ? "text.primary" : COLOR_EVIDENCIA[e.nivel] }}>{e.texto}</Typography>
                      </Stack>
                    ))}
                    {resultado.descartes?.length > 0 && (
                      <Box sx={{ pt: 1 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 800, mb: 0.3 }}>Mensajes descartados por flespi</Typography>
                        {resultado.descartes.map((d, i) => (
                          <Typography key={`${d.ts}-${i}`} sx={{ fontSize: 11.5, color: "error.main" }}>{fechaHora(d.ts)}: {d.texto}</Typography>
                        ))}
                      </Box>
                    )}
                  </Stack>
                )}

                {pestanaActiva === "mapa" && (conRecorrido ? (
                  <MapaRecorridoDiagnostico mensajes={mensajes} icono={iconoUnidad} umbralHueco={frecuencia.umbral} />
                ) : (
                  <Stack spacing={1}>
                    <Alert severity="warning" sx={{ py: 0, "& .MuiAlert-message": { fontSize: 12 } }}>
                      Los mensajes cargados no traen posición GPS válida.
                      {fixGps ? ` La última posición GPS válida es del ${fechaHora(fixGps.ts)} (hace ${hace(fixGps.ts)}).` : " No hay posiciones GPS válidas en los últimos 30 días."}
                      {ubicacionEmnify && " Emnify ubica el chip por la antena celular: es una referencia aproximada pero actual."}
                    </Alert>
                    <MapaPosiciones fix={fixGps} emnify={ubicacionEmnify} />
                  </Stack>
                ))}

                {pestanaActiva === "mensajes" && (
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Cargar los últimos</Typography>
                      <ToggleButtonGroup size="small" exclusive value={cantidad} onChange={(_, v) => v && setCantidad(v)} disabled={cargando || !plataforma?.id}
                        sx={{ "& .MuiToggleButton-root": { py: 0.1, px: 1, fontSize: 11 } }}>
                        {[50, 100, 300].map((n) => <ToggleButton key={n} value={n}>{n}</ToggleButton>)}
                      </ToggleButtonGroup>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>mensajes</Typography>
                      {cargando && <CircularProgress size={13} />}
                    </Stack>
                    {frecuencia.huecos.length > 0 && (
                      <Alert severity="warning" sx={{ py: 0, "& .MuiAlert-message": { fontSize: 11.5 } }}>
                        <b>Huecos sin reportar</b> (normalmente reporta cada ~{duracion(frecuencia.intervalo)}):
                        {frecuencia.huecos.map((h) => (
                          <Box key={h.desde} component="span" sx={{ display: "block" }}>{fechaHora(h.desde)} → {fechaHora(h.hasta)} · {duracion(h.duracion)}</Box>
                        ))}
                      </Alert>
                    )}
                    {!mensajes.length ? <Vacio>No hay mensajes para mostrar.</Vacio> : (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader sx={celdaSx}>
                          <TableHead>
                            <TableRow>
                              {["Recibido", "Hora del equipo", "Alimentación", "Batería", "Señal", "Satélites", "GPS", "Ignición", ""].map((h) => (
                                <TableCell key={h} sx={{ fontWeight: 900 }} align={["Recibido", "Hora del equipo"].includes(h) ? "left" : "right"}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {mensajes.map((m, i) => {
                              const desfase = m.ts_equipo && Math.abs(m.ts - m.ts_equipo) > 600; // >10 min: mensaje en búfer o reloj del equipo mal
                              return (
                                <TableRow key={`${m.ts}-${i}`} hover>
                                  <TableCell>{fechaHora(m.ts, true)}</TableCell>
                                  <TableCell sx={{ color: desfase ? "warning.main" : "text.secondary" }} title={desfase ? "Difiere más de 10 min de la hora de llegada: mensaje guardado en el equipo y enviado después, o reloj del equipo mal" : undefined}>
                                    {m.ts_equipo ? fechaHora(m.ts_equipo, true) : "-"}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: m.voltaje !== null && m.voltaje < 3 ? "error.main" : undefined }}>{valor(m.voltaje, " V")}</TableCell>
                                  <TableCell align="right">{m.bateria_nivel !== null ? valor(m.bateria_nivel, " %") : valor(m.bateria_voltaje, " V")}</TableCell>
                                  <TableCell align="right" sx={{ color: m.senal !== null && m.senal < 25 ? "warning.main" : undefined }}>{m.senal === null ? "-" : `${Math.round(m.senal)} %`}</TableCell>
                                  <TableCell align="right">{valor(m.satelites)}</TableCell>
                                  <TableCell align="right" sx={{ color: m.gps_valido === false ? "warning.main" : undefined }}>{m.gps_valido === null ? "-" : m.gps_valido ? "Válido" : "Sin señal"}</TableCell>
                                  <TableCell align="right">{m.ignicion === null ? "-" : m.ignicion ? "ON" : "OFF"}</TableCell>
                                  <TableCell align="right" sx={{ width: 28 }}>
                                    {m.latitud !== null && m.longitud !== null && (m.latitud || m.longitud) ? (
                                      <Tooltip title="Ver en Google Maps">
                                        <IconButton size="small" component="a" href={mapsUrl(m)} target="_blank" rel="noreferrer" sx={{ p: 0.2 }}><MapOutlinedIcon sx={{ fontSize: 15 }} /></IconButton>
                                      </Tooltip>
                                    ) : null}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Stack>
                )}

                {pestanaActiva === "grafico" && (
                  <Stack spacing={1.25}>
                    <GraficoSerie titulo="Alimentación externa" datos={grafico} clave="voltaje" unidad=" V" color={colores[0]} />
                    <GraficoSerie titulo="Señal celular" datos={grafico} clave="senal" unidad=" %" color={colores[1]} />
                    <GraficoSerie titulo="Batería interna" datos={grafico} clave={grafico.some((d) => d.bateria_nivel !== null && d.bateria_nivel !== undefined) ? "bateria_nivel" : "bateria_voltaje"}
                      unidad={grafico.some((d) => d.bateria_nivel !== null && d.bateria_nivel !== undefined) ? " %" : " V"} color={colores[0]} />
                    <GraficoSerie titulo="Satélites" datos={grafico} clave="satelites" unidad="" color={colores[1]} />
                    {!["voltaje", "senal", "bateria_nivel", "bateria_voltaje", "satelites"].some((k) => grafico.filter((d) => d[k] !== null && d[k] !== undefined).length >= 2) && (
                      <Vacio>Este equipo no informa voltaje, señal, batería ni satélites: no hay nada para graficar.</Vacio>
                    )}
                  </Stack>
                )}

                {pestanaActiva === "conexiones" && (
                  !conexiones.length ? <Vacio>flespi no registra conexiones recientes de este equipo.</Vacio> : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader sx={celdaSx}>
                        <TableHead>
                          <TableRow>
                            {["Conectó", "Desconectó", "Duración", "Mensajes", "IP del equipo", "Cierre"].map((h) => (
                              <TableCell key={h} sx={{ fontWeight: 900 }} align={["Duración", "Mensajes", "Cierre"].includes(h) ? "right" : "left"}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {conexiones.map((c) => (
                            <TableRow key={c.id} hover>
                              <TableCell>{fechaHora(c.inicio, true)}</TableCell>
                              <TableCell>{c.fin ? fechaHora(c.fin, true) : <Chip size="small" color="success" label="Conectado" sx={{ height: 18, fontSize: 10 }} />}</TableCell>
                              <TableCell align="right">{c.duracion !== null ? duracion(c.duracion) : "-"}</TableCell>
                              <TableCell align="right" sx={{ color: c.fin && !c.mensajes ? "warning.main" : undefined }}>{c.mensajes ?? "-"}</TableCell>
                              <TableCell>{c.ip || "-"}{c.transporte ? ` (${c.transporte})` : ""}</TableCell>
                              <TableCell align="right" title="Código de cierre de flespi (no documentado públicamente)">{c.close_code ?? "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                )}

                {pestanaActiva === "chip" && (
                  em ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.5, columnGap: 3 }}>
                      {[
                        ["Conexión", [em.conexion, em.tecnologia].filter(Boolean).join(" ") || "-"],
                        ["Operador", [em.red, em.pais].filter(Boolean).join(", ") || "-"],
                        ["Última actividad de red", em.ultima_red ? `${fechaHora(em.ultima_red)} (hace ${hace(em.ultima_red)})` : "-"],
                        ["Sesión de datos activa", em.sesion_datos || "-"],
                        ["Estado SIM", em.estado_sim || "-"],
                        ["Estado endpoint", em.estado_endpoint || "-"],
                        ["ICCID", em.iccid || resultado.iccid || "-"],
                        ["MSISDN", em.msisdn || "-"],
                        ["IMEI (según Emnify)", em.imei || "-"],
                        ["Tarifa / perfil", [em.tarifa, em.perfil].filter(Boolean).join(" / ") || "-"],
                        ["Consumo mes actual", em.consumo_mes_actual_mb !== null ? `${Number(em.consumo_mes_actual_mb).toFixed(2)} MB` : "-"],
                        ["Consumo mes anterior", em.consumo_mes_anterior_mb !== null ? `${Number(em.consumo_mes_anterior_mb).toFixed(2)} MB` : "-"],
                      ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between" spacing={1} sx={{ borderBottom: 1, borderColor: "divider", py: 0.35 }}>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{k}</Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, textAlign: "right" }}>{v}</Typography>
                        </Stack>
                      ))}
                    </Box>
                  ) : (
                    <Vacio>El equipo usa el chip con ICCID {resultado.iccid}. No es un chip Emnify, así que no hay datos de la operadora (estado, red, consumo).</Vacio>
                  )
                )}

                {pestanaActiva === "crudos" && (
                  !resultado.parametros ? <Vacio>No hay datos crudos del último mensaje.</Vacio> : (
                    <Stack spacing={0.75}>
                      <TextField size="small" placeholder="Buscar parámetro o valor" value={filtroCrudos} onChange={(e) => setFiltroCrudos(e.target.value)}
                        sx={{ maxWidth: 280, "& .MuiInputBase-input": { py: 0.5, fontSize: 12 } }} />
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader sx={celdaSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 900, width: "40%" }}>Parámetro</TableCell>
                              <TableCell sx={{ fontWeight: 900 }}>Valor</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {crudos.map(([k, v]) => (
                              <TableRow key={k} hover>
                                <TableCell sx={{ fontFamily: "monospace" }}>{k}</TableCell>
                                <TableCell sx={{ whiteSpace: "normal !important", wordBreak: "break-word" }}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  )
                )}
              </Box>
            </Box>

            <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
              {remoto
                ? `Causa probable a partir de los datos de ${plataforma?.nombre}${resultado.emnify ? " y Emnify" : ""}${esFlespi ? "" : " (Wialon no expone el registro de conexiones)"}. Cuando el equipo deja de comunicarse por completo, varias causas se ven igual desde el servidor.`
                : "Diagnóstico con el último mensaje de la tarjeta: no se pudieron cargar los mensajes de la unidad."}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        {resultado?.ubicacion && (
          <Button size="small" startIcon={<MapOutlinedIcon />} href={mapsUrl(resultado.ubicacion)} target="_blank" rel="noreferrer" sx={{ mr: "auto" }}>
            Última ubicación
          </Button>
        )}
        {reinicio && (
          <Tooltip title={`Enviar "${reinicio.titulo}" al equipo por flespi`}>
            <Button size="small" color="warning" startIcon={<RestartAltIcon />} onClick={() => setConfirmarReinicio(true)} disabled={cargando}>Reiniciar equipo</Button>
          </Tooltip>
        )}
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={copiarReporte} disabled={!resultado}>Copiar reporte</Button>
        <Button size="small" variant="contained" disableElevation onClick={onClose}>Cerrar</Button>
      </DialogActions>
      <Dialog open={confirmarReinicio && !!reinicio} onClose={reiniciando ? undefined : () => setConfirmarReinicio(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 900, pb: 1 }}>¿Reiniciar {nombreEquipo || "el equipo"}?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13 }}>
            Se enviará el comando <b>{reinicio?.titulo}</b> por flespi. El equipo dejará de reportar unos segundos mientras se reinicia.
          </Typography>
          {reinicio?.descripcion && <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>{reinicio.descripcion}.</Typography>}
          {enCola && (
            <Alert severity="info" sx={{ mt: 1.25, py: 0, "& .MuiAlert-message": { fontSize: 12 } }}>
              El equipo no está conectado a flespi ahora: el comando queda en cola y se envía cuando se conecte. Si no se conecta en 1 hora, se descarta.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setConfirmarReinicio(false)} disabled={reiniciando}>Cancelar</Button>
          <Button size="small" variant="contained" color="warning" disableElevation onClick={reiniciar} disabled={reiniciando}
            startIcon={reiniciando ? <CircularProgress size={14} color="inherit" /> : <RestartAltIcon />}>
            {enCola ? "Dejar en cola" : "Reiniciar ahora"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!avisoReinicio} autoHideDuration={8000} onClose={() => setAvisoReinicio(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {avisoReinicio ? <Alert severity={avisoReinicio.severidad} variant="filled" onClose={() => setAvisoReinicio(null)} sx={{ fontSize: 12.5 }}>{avisoReinicio.texto}</Alert> : <span />}
      </Snackbar>
      <Snackbar open={copiado} autoHideDuration={2500} onClose={() => setCopiado(false)} message="Reporte copiado: listo para pegar en WhatsApp o correo" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Dialog>
  );
}
